import React, {useState, useEffect} from 'react';
import Alert from '../../../../Components/Alert';
import { useFeedback, useRecommendPrompt } from '../../../../Data/Help';
import { isLocal, useNewWindow } from '../../../../Services/helpers';
import Button from '../../../../Components/Button/Button';
import Suggestion from '../../../More/Suggestion';
import Modal from '../../../../Components/Modal';
import Icon from '../../../../Components/Icons/Icon';
import { useNavigate } from 'react-router-dom';
import { useMobile } from '../../../../Components/Mobile';
import { useSyncedPreference } from '../../../../Data/Preference';

const SCORE_THRESHOLD = isLocal() ? 1 : 15;
const DISMISSED_RESET_DAYS = isLocal() ? 2 : 30;

const Recommend = ({theJourney}) => {
    const {journey} = theJourney;
    const [showing, setShowing] = useState(false);
    const [dismissed, setDismissed, isLoading] = useSyncedPreference('recommendDismissed', false);

    useEffect(() => {
        if (!journey) {
            return;
        }

        if (journey?.activity?.score > SCORE_THRESHOLD) {
            setShowing(true);
        }
    }, [journey]);

    useEffect(() => {
        if (!dismissed) {
            return;
        }

        const daysSinceDismissed = Math.floor((new Date() - new Date(dismissed)) / (1000 * 60 * 60 * 24));

        if (daysSinceDismissed > DISMISSED_RESET_DAYS) {
            setDismissed(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dismissed]);

    const onDismissed = () => {
        setShowing(false);
        const date = new Date();
        setDismissed(date.getFullYear()+'-'+(date.getMonth()+1)+'-'+date.getDate());
    }

    if (!showing || dismissed || isLoading) {
        return null;
    }

    return <Prompt onDismissed={onDismissed} />
}

const Prompt = ({onDismissed}) => {
    const {activityId} = useRecommendPrompt();
    const {giveFeedback, feedback, isLoading} = useFeedback(activityId);
    const [showingSuggestion, setShowingSuggestion] = useState(false);
    const [showingReview, setShowingReview] = useState(false);

    const doFeedback = (value) => {
        giveFeedback(value);

        if (value === 1) {
            setShowingReview(true);
        } else if (value === -1) {
            setShowingSuggestion(true);
        }
    }

    const afterReviewPrompt = (reviewGiven) => {
        if (reviewGiven) {
            giveFeedback(2);
        }

        onDismissed();
    }

    return <>
        <Alert theme="primary"
            icon="Star"
            title="Help us improve!"
            message="Would you recommend getSoapy to a friend?"
            onDismiss={() => {
                doFeedback(0);
                onDismissed(false);
            }}
            buttons={[
                <Button key="no"
                    theme="light"
                    icon="ThumbsDown"
                    iconTheme={feedback === -1 ? 'solid' : 'light'}
                    label="No, thanks"
                    disabled={isLoading}
                    loading={isLoading && feedback === -1}
                    onClick={() => doFeedback(-1)}
                    className="mb-0"
                />,
                <Button key="yes"
                    theme="light"
                    icon="ThumbsUp"
                    iconTheme={feedback === 1 ? 'solid' : 'light'}
                    label="Absolutely!"
                    disabled={isLoading}
                    loading={isLoading && feedback === 1}
                    onClick={() => doFeedback(1)}
                    className="mb-0"
                />,
            ]}
        />
        <Suggestion isOpen={showingSuggestion} setIsOpen={setShowingSuggestion} />
        <LeaveAReview isOpen={showingReview} setIsOpen={setShowingReview} after={afterReviewPrompt} />
    </>
}

const LeaveAReview = ({isOpen, setIsOpen, after}) => {
    const newWindow = useNewWindow();
    const navigate = useNavigate();
    const {isMobile, isIos, isAndroid} = useMobile();

    const select = (choice) => {
        if (choice === 'iOS') {
            newWindow('https://apps.apple.com/gb/app/getsoapy/id1636552800?action=write-review');
            after(true);

        } else if (choice === 'Android') {
            newWindow('https://play.google.com/store/apps/details?id=com.getsoapy');
            after(true);

        } else if (choice === 'Google') {
            newWindow('https://g.page/r/CSiF_6VyqCO6EB0/review');
            after(true);

        } else if (choice === 'TrustPilot') {
            newWindow('https://au.trustpilot.com/review/getsoapy.co.uk');
            after(true);

        } else if (choice === 'Refer') {
            navigate('/refer-a-friend');
            after(true);
        } else if (choice === 'NotNow') {
            after(false);
        }

        setIsOpen(false);
    }

    return <Modal title="Thanks, that's great!" isOpen={isOpen} setIsOpen={setIsOpen} dismissible>
        <div className="prose text-center text-balance">
            <p>If you have a moment, a review would be super helpful 🙏</p>
        </div>

        <div className="flex flex-col justify-center gap-x-4 mt-4 w-full">
            {(isMobile && isIos) && <ReviewButton icon="Apple"
                title="Review on the App Store"
                onClick={() => select('iOS')}
                external
            />}
            {(isMobile && isAndroid) && <ReviewButton icon="Android"
                title="Review on the Play Store"
                onClick={() => select('Android')}
                external
            />}
            <ReviewButton icon="Google"
                title="Review on Google"
                onClick={() => select('Google')}
                external
            />
            {!isMobile && <ReviewButton icon="TrustPilot"
                title="Review on Trust Pilot"
                onClick={() => select('TrustPilot')}
                external
            />}
            <ReviewButton icon="User"
                title="Refer a friend"
                subtitle="Get a two months free for every friend you recommend!"
                onClick={() => select('Refer')}
            />
            <ReviewButton icon={"XMark"}
                title="Not just now, thanks"
                onClick={() => select('NotNow')}
            />
        </div>
    </Modal>
}

const ReviewButton = ({onClick, title, subtitle, icon, external}) => {
    return <button
        className="w-full my-3 py-3 px-6 flex items-top border rounded-xl focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        onClick={onClick}
    >
        <Icon icon={icon} theme={external ? 'brands' : 'light'} className="fill-brand" size="2x" />

        <span className="grow ml-4 text-left">
            <strong className={icon === 'XMark' ? 'text-muted' : ''}>{title}</strong><br />
            <span>{subtitle}</span>
        </span>
        <span className="grow-0 h-full fill-muted">
            {external && <Icon size="2x" icon="LinkExternal" />}
        </span>
    </button>
}

export default Recommend;
